import axios from "axios";
import { SERVER_ROUTE } from "../../routes/api/api_routes";
import { store } from "../store/store";
import {
  setActivationAccountFail,
  setActivationAccountSuccess,
  setAuthenticatedFail,
  setAuthenticatedSuccess,
  setLoginFail,
  setLoginSucess,
  setPasswordResetConfirmFail,
  setPasswordResetConfirmSuccess,
  setPasswordResetFail,
  setPasswordResetSuccess,
  setRegisteringFail,
  setRegisteringSuccess,
  setUserLoadFail,
  setUserLoadSuccess,
} from "../reducers/auth";

// --- Token localStorage ---
export const getToken = () => {
  try {
    const raw = localStorage.getItem("auth");
    if (!raw) return null;
    const auth = JSON.parse(raw);
    return auth?.access || null;
  } catch (e) {
    console.error("Erreur parsing auth:", e);
    return null;
  }
};

export const setToken = (token) => {
  localStorage.setItem("auth", JSON.stringify({ access: token }));
  axios.defaults.headers.common["Authorization"] = `JWT ${token}`;
};

export const removeToken = async () => {
  try {
    localStorage.removeItem("auth");
    localStorage.removeItem("currentUser");
    delete axios.defaults.headers.common["Authorization"];
    await new Promise((r) => setTimeout(r, 50)); // petit delay
  } catch (error) {
    console.error("Erreur lors de la suppression du token :", error);
  }
};

// --- Utilisateur localStorage ---
export const getCurrentUser = () => {
  try {
    const user = localStorage.getItem("currentUser");
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error("Erreur lors de la récupération de l'utilisateur :", error);
    return null;
  }
};

export const setCurrentUser = (user) => {
  localStorage.setItem("currentUser", JSON.stringify(user));
};

export const removeCurrentUser = () => {
  localStorage.removeItem("currentUser");
};

// --- Rôles ---
export const isAdmin = () => getCurrentUser()?.is_admin === true;
export const isCoach = () => getCurrentUser()?.is_coach || false;

// --- Session ---
export const checkSession = () => {
  const user = getCurrentUser();
  return !!(getToken() && user);
};

export const isAuthenticated = () => !!getToken();

// --- Axios global config ---
export const configureAxios = () => {
  axios.defaults.baseURL = SERVER_ROUTE;
  axios.defaults.timeout = 10000;
  axios.defaults.headers.common["Content-Type"] = "application/json";

  const token = getToken();
  if (token) {
    axios.defaults.headers.common["Authorization"] = `JWT ${token}`;
  }

  axios.interceptors.response.use(
    (res) => res,
    async (error) => {
      if (error.response?.status === 401) {
        await removeToken();
        removeCurrentUser();
      }
      return Promise.reject(error);
    }
  );
};

// --- Auth object fusionné ---
const auth = {
  async isAuthenticated() {
    return !!getToken();
  },

  async checkAuthenticated() {
    const dispatch = store.dispatch;
    const token = getToken();
    if (!token) return dispatch(setAuthenticatedFail());

    try {
      const res = await axios.post("/auth/jwt/verify/", { token });
      if (res.data.code !== "token_not_valid") {
        dispatch(setAuthenticatedSuccess(res.data));
      } else {
        dispatch(setAuthenticatedFail());
      }
    } catch {
      dispatch(setAuthenticatedFail());
    }
  },

  async load_user() {
    const dispatch = store.dispatch;
    try {
      const res = await axios.get("/auth/users/me/");
      dispatch(setUserLoadSuccess(res.data));
      setCurrentUser(res.data);
    } catch {
      dispatch(setUserLoadFail({ user: null }));
    }
  },

  async Login(email, password) {
    const dispatch = store.dispatch;
    try {
      const res = await axios.post("/auth/jwt/create/", { email, password });
      const token = res.data?.access;
      if (!token)
        return dispatch(setLoginFail({ access: null, refresh: null }));

      setToken(token);
      dispatch(setLoginSucess(res.data));

      await this.checkAuthenticated();
      await this.load_user();
    } catch {
      dispatch(setLoginFail({ access: null, refresh: null }));
    }
  },

  async logout(navigateCallback) {
    const dispatch = store.dispatch;
    await removeToken();
    removeCurrentUser();
    dispatch(setAuthenticatedFail());
    dispatch(setUserLoadFail({ user: null }));

    if (navigateCallback) {
      navigateCallback("/login");
    }
  },

  async signup(lastName, firstName, email, password, re_password) {
    const dispatch = store.dispatch;
    try {
      const res = await axios.post(`${process.env.REACT_APP_SERVER_ROUTE}/auth/users/`, {
        lastName,
        firstName,
        email,
        password,
        re_password,
      });
      dispatch(setRegisteringSuccess(res.data));
    } catch {
      dispatch(setRegisteringFail());
    }
  },

  async verify(uid, token) {
    const dispatch = store.dispatch;
    try {
      const res = await axios.post(`${process.env.REACT_APP_SERVER_ROUTE}/auth/users/activation/`, { uid, token });
      dispatch(setActivationAccountSuccess(res.data));
    } catch {
      dispatch(setActivationAccountFail());
    }
  },

  async reset_password(email) {
    const dispatch = store.dispatch;
    try {
      const res = await axios.post(`${process.env.REACT_APP_SERVER_ROUTE}/auth/users/reset_password/`, { email });
      dispatch(setPasswordResetSuccess(res.data));
    } catch {
      dispatch(setPasswordResetFail());
    }
  },

  async reset_password_confirm(uid, token, new_password, re_new_password) {
    const dispatch = store.dispatch;
    try {
      const res = await axios.post("/auth/users/reset_password_confirm/", {
        uid,
        token,
        new_password,
        re_new_password,
      });
      dispatch(setPasswordResetConfirmSuccess(res.data));
    } catch {
      dispatch(setPasswordResetConfirmFail());
    }
  },
};

export default auth;
